import { Box, Button, Typography, useMediaQuery } from '@mui/material'
import { Slider } from 'components/Slider/Slider'
import theme from 'lib/theme'
import { Dispatch, SetStateAction, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ConfigFieldTypes,
  DefaultConfig,
  DefaultConfigFields,
} from './SearchConfig'
import { ExportConfigDialog } from './ExportConfigDialog'

type ConfigAdjustmentProps = {
  handleSubmitConfig: (values: ConfigFieldTypes) => void
  values: ConfigFieldTypes
  setFieldValue: (field: string, value: number | number[]) => void
  defaultBoostConfig: DefaultConfig | undefined
  setBoostConfig: Dispatch<SetStateAction<DefaultConfig | undefined>>
}

const sortObjAlphabetically = (obj: DefaultConfigFields) => {
  const sorted = Object.keys(obj)
    .sort()
    .reduce<Partial<DefaultConfigFields>>((acc, key) => {
      acc[key as keyof DefaultConfigFields] =
        obj[key as keyof DefaultConfigFields]
      return acc
    }, {})
  return sorted
}

export const ConfigAdjustment = ({
  handleSubmitConfig,
  values,
  setFieldValue,
  defaultBoostConfig,
  setBoostConfig,
}: ConfigAdjustmentProps) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialTextToCopy = {
    boosts: {
      fields: {
        name: values.name,
        name_keyword: values.nameKeyword,
        keywords: values.keywords,
        brand: values.brand,
        description: values.description,
        functional_name: values.functionalName,
        categories_name: values.categoriesName,
        categories_parent_name: values.categoriesParentName,
        gtin: values.gtin,
      },
      min_score: values.minScore,
    },
  }
  const [textToCopy, setTextToCopy] = useState(initialTextToCopy)

  useEffect(() => {
    setTextToCopy({
      boosts: {
        fields: {
          name: values.name,
          name_keyword: values.nameKeyword,
          keywords: values.keywords,
          brand: values.brand,
          description: values.description,
          functional_name: values.functionalName,
          categories_name: values.categoriesName,
          categories_parent_name: values.categoriesParentName,
          gtin: values.gtin,
        },
        min_score: values.minScore,
      },
    })
  }, [values])

  const sliderArr = [
    { label: t('namePartial'), name: 'name' },
    { label: t('nameExact'), name: 'nameKeyword' },
    { label: t('brand'), name: 'brand' },
    { label: t('keywords'), name: 'keywords' },
    { label: t('functionalName'), name: 'functionalName' },
    { label: t('desc'), name: 'description' },
    { label: t('categoryName'), name: 'categoriesName' },
    { label: t('parentCategoryName'), name: 'categoriesParentName' },
    { label: t('GTIN'), name: 'gtin' },
  ]

  const disableReset = () => {
    const boostConfigValues = {
      gtin: defaultBoostConfig?.fields.gtin || 1,
      categoriesParentName:
        defaultBoostConfig?.fields.categoriesParentName || 1,
      categoriesName: defaultBoostConfig?.fields.categoriesName || 1,
      description: defaultBoostConfig?.fields.description || 1,
      functionalName: defaultBoostConfig?.fields.functionalName || 1,
      keywords: defaultBoostConfig?.fields.keywords || 1,
      name: defaultBoostConfig?.fields.name || 1,
      nameKeyword: defaultBoostConfig?.fields.nameKeyword || 1,
      brand: defaultBoostConfig?.fields.brand || 1,
      minScore: defaultBoostConfig?.minScore || 1,
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { categoryTree, search, store, ...boostFormValues } = values
    return (
      JSON.stringify(sortObjAlphabetically(boostConfigValues)) ===
      JSON.stringify(sortObjAlphabetically(boostFormValues))
    )
  }

  const handleReset = () => {
    setBoostConfig(defaultBoostConfig)
    sliderArr.forEach((field) => {
      setFieldValue(
        field.name,
        (defaultBoostConfig?.fields[
          field.name as keyof Partial<DefaultConfigFields>
        ] as number) || 1
      )
    })
    setFieldValue('minScore', defaultBoostConfig?.minScore || 1)
  }

  const handleSubmit = () => {
    handleSubmitConfig(values)
  }

  return (
    <Box
      height='100%'
      width={isMobile ? '100%' : '400px'}
      p={2}
      sx={{
        overflowY: 'auto',
        borderLeft: isMobile
          ? 'none'
          : `1px solid ${theme.palette.action.disabled}`,
        display: 'flex',
        justifyContent: 'center',
      }}>
      <Box width='100%'>
        <Typography variant='h2'>{t('searchConfiguration')}</Typography>
        <Typography
          mt={3}
          mb={2}
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '10px',
            fontWeight: 'bold',
          }}>
          {t('fieldWeights').toUpperCase()}
        </Typography>
        <Box>
          {sliderArr.map((item) => {
            const { label, name } = item
            return (
              <Slider
                key={name}
                name={name}
                value={
                  (values[name as keyof Partial<ConfigFieldTypes>] as number) ??
                  1
                }
                setFieldValue={setFieldValue}
                ariaLabel={t('configSettings', { label })}
                maxValue={50}
                minValue={1}
                label={label}
              />
            )
          })}
          <Typography
            mt={3}
            mb={2}
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 'bold',
              fontSize: '10px',
            }}>
            {t('cutOff').toUpperCase()}
          </Typography>
          <Slider
            name='minScore'
            value={
              (values[
                'minScore' as keyof Partial<ConfigFieldTypes>
              ] as number) ?? 1
            }
            setFieldValue={setFieldValue}
            ariaLabel={t('configSettings', { label: t('minScore') })}
            maxValue={50}
            minValue={1}
            label={t('minScore')}
          />
        </Box>
        <Box mt={3}>
          <Button
            data-testid='Reset configuration'
            disabled={disableReset()}
            onClick={handleReset}
            sx={{
              width: '100%',
              padding: '8px 2px',
              fontSize: '10px',
              color: theme.palette.commonColors.black,
              backgroundColor: theme.palette.neutral.grey9,
              '&:hover': {
                backgroundColor: theme.palette.action.disabled,
                color: theme.palette.commonColors.black,
              },
            }}>
            {t('resetToCurrentConfig')}
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{
              width: '100%',
              padding: '8px 2px',
              fontSize: '10px',
              marginTop: theme.spacing(1),
            }}>
            {t('testConfig')}
          </Button>
          <Button
            onClick={() => {
              setDialogOpen(true)
            }}
            sx={{
              width: '100%',
              padding: '8px 2px',
              fontSize: '10px',
              marginTop: theme.spacing(1),
            }}>
            {t('exportConfig')}
          </Button>
        </Box>
        <ExportConfigDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          textToCopy={textToCopy}
        />
      </Box>
    </Box>
  )
}
